import { useEffect, useMemo, useRef, useState } from 'react'
import { Check, ChevronDown, ChevronLeft, ChevronRight, Search } from 'lucide-react'

// /platform FilterBar 와 동일한 팝오버 패턴을 단일선택/월피커로.
function useClickOutside(onOutside: () => void) {
  const ref = useRef<HTMLDivElement>(null)
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside()
    }
    const k = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onOutside()
    }
    document.addEventListener('mousedown', h)
    document.addEventListener('keydown', k)
    return () => {
      document.removeEventListener('mousedown', h)
      document.removeEventListener('keydown', k)
    }
  }, [onOutside])
  return ref
}

const triggerCls =
  'inline-flex min-h-[36px] items-center gap-2 rounded-r-1 border border-line-soft bg-surface-base py-1.5 pl-3 pr-2.5 lf-body-sm hover:bg-surface-soft focus:shadow-focus focus:outline-none'

function Trigger({ label, valueText, open }: { label: string; valueText: string; open: boolean }) {
  return (
    <>
      <span className="text-fg-3">{label}</span>
      <span className="max-w-[160px] truncate text-fg-1">{valueText}</span>
      <ChevronDown
        size={15}
        className={`ml-0.5 shrink-0 text-fg-3 transition-transform duration-fast ${open ? 'rotate-180' : ''}`}
      />
    </>
  )
}

export interface SelectOption {
  value: string
  label: string
}

// 단일선택 드롭다운 (레이블·담당자·크리에이터). value '' = 전체.
export function SelectDropdown({
  label,
  placeholder,
  value,
  options,
  onChange,
  searchable,
}: {
  label: string
  placeholder: string
  value: string
  options: SelectOption[]
  onChange: (v: string) => void
  searchable?: boolean
}) {
  const [open, setOpen] = useState(false)
  const [q, setQ] = useState('')
  const ref = useClickOutside(() => setOpen(false))
  const current = options.find((o) => o.value === value)
  const valueText = current ? current.label : placeholder

  const shown = useMemo(() => {
    const k = q.trim().toLowerCase()
    const list = k ? options.filter((o) => o.label.toLowerCase().includes(k)) : options
    return list.slice(0, 300)
  }, [options, q])

  const pick = (v: string) => {
    onChange(v)
    setOpen(false)
    setQ('')
  }

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        className={triggerCls}
      >
        <Trigger label={label} valueText={valueText} open={open} />
      </button>

      {open && (
        <div
          role="listbox"
          className="absolute z-30 mt-1 max-h-80 w-60 overflow-hidden rounded-r-2 border border-line-soft bg-surface-base shadow-lf-pop"
        >
          {searchable && (
            <div className="border-b border-line-hairline p-2">
              <div className="flex items-center gap-1.5 rounded-r-1 border border-line-soft bg-surface-base px-2">
                <Search size={14} className="shrink-0 text-fg-3" />
                <input
                  autoFocus
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="이름 검색"
                  className="min-h-[32px] w-full bg-transparent py-1 lf-body-sm text-fg-1 outline-none placeholder:text-fg-3"
                />
              </div>
            </div>
          )}
          <div className="max-h-64 overflow-auto py-1">
            <button
              type="button"
              onClick={() => pick('')}
              className="flex w-full items-center justify-between px-3 py-1.5 text-left lf-body-sm hover:bg-surface-soft"
            >
              <span className={value === '' ? 'font-medium text-fg-1' : 'text-fg-2'}>{placeholder}</span>
              {value === '' && <Check size={14} className="shrink-0 text-brand-pink" />}
            </button>
            {shown.map((o) => {
              const on = o.value === value
              return (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => pick(o.value)}
                  className="flex w-full items-center justify-between px-3 py-1.5 text-left lf-body-sm hover:bg-surface-soft"
                >
                  <span className={`truncate ${on ? 'font-medium text-fg-1' : 'text-fg-2'}`}>{o.label}</span>
                  {on && <Check size={14} className="ml-2 shrink-0 text-brand-pink" />}
                </button>
              )
            })}
            {searchable && shown.length === 0 && <div className="px-3 py-2 lf-body-sm text-fg-3">결과 없음</div>}
          </div>
        </div>
      )}
    </div>
  )
}

// 월 피커 — < 2026 > 년도 이동 + 1~12월 버튼. value/onChange = 'YYYY-MM'.
export function MonthDropdown({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  const selY = Number(value.slice(0, 4))
  const selM = Number(value.slice(5, 7))
  const [open, setOpen] = useState(false)
  const [viewYear, setViewYear] = useState(selY)
  const ref = useClickOutside(() => setOpen(false))

  const now = new Date()
  const curY = now.getFullYear()
  const curM = now.getMonth() + 1

  const openPicker = () => {
    setViewYear(selY)
    setOpen((v) => !v)
  }
  const pick = (m: number) => {
    onChange(`${viewYear}-${String(m).padStart(2, '0')}`)
    setOpen(false)
  }

  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={openPicker} aria-haspopup="dialog" aria-expanded={open} className={triggerCls}>
        <Trigger label="월" valueText={`${selY}년 ${selM}월`} open={open} />
      </button>

      {open && (
        <div className="absolute z-30 mt-1 w-60 rounded-r-2 border border-line-soft bg-surface-base p-2 shadow-lf-pop">
          <div className="mb-1 flex items-center justify-between px-1">
            <button
              type="button"
              onClick={() => setViewYear((y) => y - 1)}
              aria-label="이전 해"
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 hover:bg-surface-soft hover:text-fg-1 focus:shadow-focus focus:outline-none"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="lf-body-sm lf-num font-medium text-fg-1">{viewYear}</span>
            <button
              type="button"
              onClick={() => setViewYear((y) => Math.min(curY, y + 1))}
              disabled={viewYear >= curY}
              aria-label="다음 해"
              className="inline-flex h-8 w-8 items-center justify-center rounded-r-1 text-fg-3 hover:bg-surface-soft hover:text-fg-1 focus:shadow-focus focus:outline-none disabled:cursor-not-allowed disabled:opacity-40"
            >
              <ChevronRight size={16} />
            </button>
          </div>
          <div className="grid grid-cols-4 gap-1">
            {Array.from({ length: 12 }).map((_, i) => {
              const m = i + 1
              const future = viewYear > curY || (viewYear === curY && m > curM)
              const on = viewYear === selY && m === selM
              return (
                <button
                  key={m}
                  type="button"
                  disabled={future}
                  onClick={() => pick(m)}
                  className={`min-h-[34px] rounded-r-1 lf-body-sm lf-num focus:shadow-focus focus:outline-none ${
                    on
                      ? 'bg-brand-pink font-medium text-brand-brown'
                      : future
                        ? 'cursor-not-allowed text-fg-disabled'
                        : 'text-fg-1 hover:bg-surface-soft'
                  }`}
                >
                  {m}월
                </button>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
